"use client";
import {usePathname} from "next/navigation";
import {useEffect, useRef, useState} from "react";
import { subscribeAdsUserInteraction } from "../../../lib/ads-user-interaction-gate";

const loadedScripts = new Map();
const cleanupAdScripts = () => {
    try {
        if (typeof window === 'undefined') return;

        if ('tagEnv_336280' in window) delete window.tagEnv_336280;
        if ('tagEnv_3362801' in window) delete window.tagEnv_3362801;
        if ('tagEnv_3362802' in window) delete window.tagEnv_3362802;
        if ('znAiAdtag' in window) delete window.znAiAdtag;
        if ('_mgq' in window) delete window._mgq;

    } catch (error) {
        console.warn('Error during script cleanup:', error);
    }
};

/**
 * event: 'load' (padrão) carrega já; 'scroll' | 'interaction' espera a primeira
 * interação do usuário (scroll/toque/tecla) — tira MGID/ads do caminho crítico (LCP/TBT).
 */
const AddScriptClient = ({uniqueID, urlScript, async = true, local = 'body', addContet = false, device, event = 'load'}) => {
    const pathname = usePathname();
    const scriptLoaded = useRef(false);
    const [isClient, setIsClient] = useState(false);

    const carregaScripts = () => {
        try {
            if (typeof window === 'undefined') return;
            const currentScript = loadedScripts.get(uniqueID);
            if (currentScript && currentScript.pathname === pathname) {
                return;
            }

            if (urlScript) {
                loadedScripts.set(uniqueID, { pathname, timestamp: Date.now(), pending: true });
            }

            const existingScript = document.getElementById(uniqueID);
            if (existingScript) {
                existingScript.remove();
            }

            const existingContentScript = document.getElementById(uniqueID + 'addContet');
            if (existingContentScript) {
                existingContentScript.remove();
            }

            const script = document.createElement("script");
            script.id = uniqueID;
            script.type = 'text/javascript';
            if (local && local !== 'body' && local !== 'head') {
                script.dataset.adSlotContainer = local;
            }
            
            script.onerror = (error) => {
                console.warn(`Failed to load script: ${urlScript}`, error);
                if (script.parentNode) {
                    script.parentNode.removeChild(script);
                }
                loadedScripts.delete(uniqueID);
            };

            if(urlScript){
                script.src = urlScript;
                script.onload = () => {
                    loadedScripts.set(uniqueID, { pathname, timestamp: Date.now() });
                    scriptLoaded.current = true;
                };
            }
            if(!urlScript && addContet){
                script.text = addContet;
                addContet = false;
                loadedScripts.set(uniqueID, { pathname, timestamp: Date.now() });
                scriptLoaded.current = true;
            }
            if(async){
                script.async = true;
            }

            if(local == 'body'){
                const footer = document.getElementsByTagName("footer")[0];
                if (footer) {
                    footer.appendChild(script);
                } else {
                    document.body.appendChild(script);
                }
            }
            if(local == 'head'){
                const head = document.getElementsByTagName("head")[0];
                if (head) {
                    head.appendChild(script);
                } else if (urlScript) {
                    loadedScripts.delete(uniqueID);
                }
            }
            if(local != 'head' && local != 'body'){
                var idDiv = document.getElementById(local);
                if(idDiv){
                    idDiv.innerHTML = '';
                    idDiv.appendChild(script);	 
                } else if (urlScript) {
                    loadedScripts.delete(uniqueID);
                }
            }

            if(addContet){
                const scriptAdd = document.createElement("script");
                scriptAdd.id = uniqueID + 'addContet';
                scriptAdd.type = 'text/javascript';
                scriptAdd.async = true;
                scriptAdd.text = addContet;
                var idDivAdd = document.getElementById(uniqueID);
                if(idDivAdd){
                    idDivAdd.insertAdjacentElement('afterend', scriptAdd);	 
                }
            }
        } catch (error) {
            console.warn('Error loading script:', error);
            loadedScripts.delete(uniqueID);
        }
    };

    useEffect(() => {
        try {
            setIsClient(true);
            if (typeof window === 'undefined') return;
            scriptLoaded.current = false;
            cleanupAdScripts();
            for (const [id, data] of loadedScripts.entries()) {
                if (data.pathname !== pathname) {
                    const script = document.getElementById(id);
                    if (script) {
                        script.remove();
                    }
                    loadedScripts.delete(id);
                }
            }

            const loadNow = () => {
                if (scriptLoaded.current) return;
                const dev = device ?? 'All';
                if (dev === 'All') {
                    carregaScripts();
                } else if (window.innerWidth < 768 && dev === 'Mobile') {
                    carregaScripts();
                } else if (window.innerWidth > 768 && dev === 'Desktop') {
                    carregaScripts();
                }
            };

            const waitForInteraction =
                event === 'scroll' || event === 'interaction' || event === 'idle';
            const unsubscribe = waitForInteraction
                ? subscribeAdsUserInteraction(loadNow)
                : (loadNow(), null);

            return () => {
                try {
                    if (typeof unsubscribe === 'function') unsubscribe();
                    cleanupAdScripts();
                    const script = document.getElementById(uniqueID);
                    if (script) {
                        script.remove();
                        loadedScripts.delete(uniqueID);
                    }
                    const contentScript = document.getElementById(uniqueID + 'addContet');
                    if (contentScript) {
                        contentScript.remove();
                    }
                } catch (error) {
                    console.warn('Error during cleanup:', error);
                }
            };
        } catch (error) {
            console.warn('Error in script effect:', error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, device, event]);

    if (!isClient) {
        return null;
    }

    return null;
}

export default AddScriptClient;
